import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Loader2, Send } from "lucide-react";

/**
 * Parent AI Assistant — asks Claude for a structured assignment/worksheet/resource
 * that can be created on the parent dashboard with one click.
 */
export default function ParentAIPanel({ students, onCreated }) {
  const [message, setMessage] = useState("");
  const [intent, setIntent] = useState("assignment");
  const [studentId, setStudentId] = useState(students?.[0]?.id || "");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const ask = async (e) => {
    e.preventDefault();
    if (!message.trim()) return toast.error("Type a request first");
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/parent/ai/chat", { message, intent, student_id: studentId });
      setResult(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "AI unavailable");
    } finally { setBusy(false); }
  };

  const create = async () => {
    if (!result?.parsed) return;
    const p = result.parsed;
    try {
      if (p.type === "assignment") {
        if (!studentId) return toast.error("Pick a student first");
        await api.post("/assignments", { ...p.data, student_id: studentId });
        toast.success("Assignment created");
      } else if (p.type === "worksheet") {
        if (!studentId) return toast.error("Pick a student first");
        await api.post("/worksheets", { ...p.data, student_id: studentId });
        toast.success("Worksheet created");
      } else if (p.type === "resource") {
        await api.post("/resources", p.data);
        toast.success("Resource added to library");
      } else {
        toast("Just advice — nothing to save");
        return;
      }
      setResult(null); setMessage("");
      onCreated && onCreated();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to create"); }
  };

  return (
    <section className="p-6 rounded-3xl bg-gradient-to-br from-teal-50 to-sky-50 border border-teal-100" data-testid="parent-ai-panel">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles size={18} className="text-teal-700" />
        <h2 className="font-display font-semibold text-lg">AI teaching assistant</h2>
      </div>
      <p className="text-sm text-slate-600 mb-3">Describe what you need and I'll draft an assignment, worksheet, or resource in one tap.</p>

      <form onSubmit={ask} className="space-y-2">
        <div className="flex flex-wrap gap-2">
          {["assignment", "worksheet", "resource", "advice"].map((i) => (
            <button key={i} type="button" data-testid={`ai-intent-${i}`} onClick={()=>setIntent(i)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold capitalize ${intent === i ? "bg-teal-700 text-white" : "bg-white text-slate-700 border border-slate-200"}`}>
              {i}
            </button>
          ))}
        </div>
        <div className="grid sm:grid-cols-[1fr_auto] gap-2">
          <select data-testid="ai-student" value={studentId} onChange={(e)=>setStudentId(e.target.value)}
            className="px-3 py-2 rounded-xl border border-slate-200 bg-white text-sm">
            <option value="">Choose student (if applicable)…</option>
            {students.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <div className="flex gap-2">
          <input data-testid="ai-message" value={message} onChange={(e)=>setMessage(e.target.value)}
            placeholder="e.g., 5-question mc worksheet on fractions basics"
            className="flex-1 px-4 py-3 rounded-xl border border-slate-200 bg-white outline-none focus:border-teal-500" />
          <button data-testid="ai-send" type="submit" disabled={busy}
            className="px-5 py-3 rounded-full bg-teal-700 hover:bg-teal-800 text-white font-semibold inline-flex items-center gap-2 disabled:opacity-60">
            {busy ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />} Ask
          </button>
        </div>
      </form>

      {result && (
        <div data-testid="ai-result" className="mt-4 p-4 rounded-2xl bg-white border border-slate-200 space-y-2">
          <div className="text-xs text-slate-500 uppercase tracking-widest">Draft · {result.parsed?.type || "raw"}</div>
          {result.parsed?.type === "assignment" && (
            <>
              <div className="font-semibold text-slate-900">{result.parsed.data.title}</div>
              <div className="text-xs text-slate-500">{result.parsed.data.subject} · due {result.parsed.data.due_date || "—"}</div>
              <p className="text-sm text-slate-700">{result.parsed.data.description}</p>
            </>
          )}
          {result.parsed?.type === "worksheet" && (
            <>
              <div className="font-semibold text-slate-900">{result.parsed.data.title}</div>
              <div className="text-xs text-slate-500">{result.parsed.data.subject} · {result.parsed.data.items?.length || 0} items</div>
              <ul className="text-sm text-slate-700 list-disc ml-5">
                {(result.parsed.data.items || []).slice(0, 5).map((it, i) => <li key={i}>{it.prompt}</li>)}
              </ul>
            </>
          )}
          {result.parsed?.type === "resource" && (
            <>
              <div className="font-semibold text-slate-900">{result.parsed.data.title}</div>
              <div className="text-xs text-slate-500">{result.parsed.data.subject} · {result.parsed.data.kind}</div>
              <a href={result.parsed.data.url} target="_blank" rel="noreferrer" className="text-teal-700 text-sm break-all">{result.parsed.data.url}</a>
              {result.parsed.data.note && <p className="text-sm text-slate-700">{result.parsed.data.note}</p>}
            </>
          )}
          {result.parsed?.type === "advice" && <p className="text-sm text-slate-700">{result.parsed.data.text}</p>}
          {!result.parsed && <pre className="text-xs whitespace-pre-wrap text-slate-700">{result.raw}</pre>}

          {result.parsed && result.parsed.type !== "advice" && (
            <button data-testid="ai-create" onClick={create}
              className="mt-2 px-4 py-2 rounded-full bg-teal-700 hover:bg-teal-800 text-white text-sm font-semibold">Create this</button>
          )}
        </div>
      )}
    </section>
  );
}
